import { keepPreviousData } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import {
  formatDate,
  formatReservationDateTime,
  formatSecondOpinionDueDate,
  getLocalizedSpeciality,
} from '@virtual-care/isomorphic';
import {
  Button,
  Card,
  CardContent,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@virtual-care/shadcn';
import { Loading, ReservationStatus, useData } from '@virtual-care/ui';

import { useLanguage } from '../../i18n/use-language.ts';
import { Header, ProfileCard } from '../components/header';
import { useUser } from '../firebase.tsx';
import { useSetting } from '../hooks/use-settings.tsx';

type TabType = 'standard' | 'second_opinion' | 'homecare' | 'printing';

type UnifiedAppointment = {
  id: string;
  recordNumber: number;
  speciality: string;
  date: string;
  createdAt: string;
  status: string;
  providerName: string;
  service: TabType;
  urgent?: boolean;
};

export default function Appointments() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const homecareEnabled = useSetting('homecareEnabled');
  const isHomecareEnabled = homecareEnabled?.value === 'true';
  const settingsLoaded = homecareEnabled !== undefined;
  const { data: identity, isLoading: isLoadingIdentity } = useUser();
  const isB2B = !!identity?.b2bProfile;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabType =
    tabParam === 'second_opinion' ||
    (tabParam === 'homecare' && isHomecareEnabled) ||
    (tabParam === 'printing' && isB2B)
      ? tabParam
      : 'standard';

  useEffect(() => {
    if (isLoadingIdentity || !settingsLoaded) return;
    if (tabParam === null) return;
    const isValid =
      tabParam === 'standard' ||
      tabParam === 'second_opinion' ||
      (tabParam === 'homecare' && isHomecareEnabled) ||
      (tabParam === 'printing' && isB2B);
    if (!isValid) {
      setSearchParams({}, { replace: true });
    }
  }, [
    tabParam,
    isHomecareEnabled,
    isB2B,
    isLoadingIdentity,
    settingsLoaded,
    setSearchParams,
  ]);

  const setActiveTab = (tab: TabType) => {
    setSearchParams(tab === 'standard' ? {} : { tab }, { replace: true });
  };

  const tabs: { key: TabType; label: string }[] = [
    { key: 'standard', label: t('appointments.tabs.standard') },
    { key: 'second_opinion', label: t('appointments.tabs.secondOpinion') },
    { key: 'homecare', label: t('appointments.tabs.homecare') },
    { key: 'printing', label: t('appointments.tabs.printing') },
  ];

  const [standardPagination, setStandardPagination] = useState<PaginationState>(
    { pageIndex: 0, pageSize: 10 },
  );
  const [soPagination, setSoPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [hcPagination, setHcPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [printingPagination, setPrintingPagination] = useState<PaginationState>(
    { pageIndex: 0, pageSize: 10 },
  );

  const { data: standardData, isLoading: isLoadingStandard } = useData(
    'GET /patients/me/reservations',
    {
      page: standardPagination.pageIndex + 1,
      pageSize: standardPagination.pageSize,
    },
    { placeholderData: keepPreviousData },
  );

  const { data: soData, isLoading: isLoadingSO } = useData(
    'GET /patients/me/second-opinions',
    {
      page: soPagination.pageIndex + 1,
      pageSize: soPagination.pageSize,
    },
    { placeholderData: keepPreviousData },
  );

  const { data: hcData, isLoading: isLoadingHC } = useData(
    'GET /patients/me/homecare-reservations',
    {
      page: hcPagination.pageIndex + 1,
      pageSize: hcPagination.pageSize,
    },
    { placeholderData: keepPreviousData, enabled: isHomecareEnabled },
  );

  const { data: printingData, isLoading: isLoadingPrinting } = useData(
    'GET /patients/me/printing-requests',
    {
      page: printingPagination.pageIndex + 1,
      pageSize: printingPagination.pageSize,
    },
    { placeholderData: keepPreviousData, enabled: isB2B },
  );

  const standardRecords: UnifiedAppointment[] = useMemo(
    () =>
      (standardData?.records || []).map((r) => ({
        id: r.id,
        recordNumber: r.reservationNumber,
        service: 'standard' as const,
        speciality: getLocalizedSpeciality(r.speciality, language).name,
        date: formatReservationDateTime(r),
        createdAt: r.createdAt,
        status: r.status,
        providerName: r.doctor
          ? `${r.doctor.firstName} ${r.doctor.lastName}`
          : t('common.notSpecified'),
      })),
    [language, standardData?.records, t],
  );

  const soRecords: UnifiedAppointment[] = useMemo(
    () =>
      (soData?.records || []).map((r) => ({
        id: r.id,
        recordNumber: r.counter,
        service: 'second_opinion' as const,
        speciality: getLocalizedSpeciality(r.speciality, language).name,
        date: formatSecondOpinionDueDate(r),
        createdAt: r.createdAt,
        status: r.status,
        providerName: r.doctor
          ? `${r.doctor.firstName} ${r.doctor.lastName}`
          : t('common.notSpecified'),
        urgent: r.urgent,
      })),
    [language, soData?.records, t],
  );

  const hcRecords: UnifiedAppointment[] = useMemo(
    () =>
      (hcData?.records || []).map((r) => {
        const vendorName =
          r.vendor.vendorType === 'INDIVIDUAL'
            ? `${r.vendor.individualProfile?.firstName ?? ''} ${r.vendor.individualProfile?.lastName ?? ''}`.trim() ||
              t('common.notSpecified')
            : (r.vendor.clinicProfile?.clinicName ?? t('common.notSpecified'));
        return {
          id: r.id,
          recordNumber: r.counter,
          service: 'homecare' as const,
          speciality: r.vendorService.service.name,
          date: formatReservationDateTime(r),
          createdAt: r.createdAt,
          status: r.status,
          providerName: vendorName,
        };
      }),
    [hcData?.records, t],
  );

  const printingRecords: UnifiedAppointment[] = useMemo(
    () =>
      (printingData?.records || []).map((r) => ({
        id: r.id,
        recordNumber: r.counter,
        service: 'printing' as const,
        speciality: '',
        date: '',
        createdAt: r.createdAt,
        status: r.status,
        providerName: '',
      })),
    [printingData?.records],
  );

  const activeRecords = {
    standard: standardRecords,
    second_opinion: soRecords,
    homecare: hcRecords,
    printing: printingRecords,
  }[activeTab];
  const activePageCount =
    {
      standard: standardData?.pageCount,
      second_opinion: soData?.pageCount,
      homecare: hcData?.pageCount,
      printing: printingData?.pageCount,
    }[activeTab] || 0;
  const activeLoading = {
    standard: isLoadingStandard,
    second_opinion: isLoadingSO,
    homecare: isLoadingHC,
    printing: isLoadingPrinting,
  }[activeTab];
  const activePagination = {
    standard: standardPagination,
    second_opinion: soPagination,
    homecare: hcPagination,
    printing: printingPagination,
  }[activeTab];
  const activePaginationSetter = {
    standard: setStandardPagination,
    second_opinion: setSoPagination,
    homecare: setHcPagination,
    printing: setPrintingPagination,
  }[activeTab];

  const handleRowClick = useCallback(
    (record: UnifiedAppointment) => {
      if (record.service === 'standard') {
        navigate(`/appointments/${record.id}`);
      } else if (record.service === 'second_opinion') {
        navigate(`/second-opinion/${record.id}`);
      } else if (record.service === 'homecare') {
        navigate(`/homecare/reservations/${record.id}`);
      } else {
        navigate(`/printing/${record.id}`);
      }
    },
    [navigate],
  );

  const columns: ColumnDef<UnifiedAppointment>[] = useMemo(() => {
    const isHomecare = activeTab === 'homecare';
    const isSecondOpinion = activeTab === 'second_opinion';
    const isPrinting = activeTab === 'printing';

    const statusColumn: ColumnDef<UnifiedAppointment> = {
      accessorKey: 'status',
      header: t('appointments.columns.status'),
      cell: ({ row }) => (
        <ReservationStatus.Root
          status={row.original.status}
          className="rounded-xl px-3 py-1 md:rounded-full"
        >
          <ReservationStatus.Label
            render={(meta) => t(meta.key)}
          />
        </ReservationStatus.Root>
      ),
    };

    if (isPrinting) {
      return [
        {
          accessorKey: 'recordNumber',
          header: t('appointments.columns.recordNumber'),
          cell: ({ row }) => <span>{row.getValue('recordNumber')}</span>,
        },
        {
          accessorKey: 'createdAt',
          header: t('appointments.columns.createdAt'),
          cell: ({ row }) => (
            <span className="text-gray-600">
              {formatDate(row.original.createdAt)}
            </span>
          ),
        },
        statusColumn,
      ];
    }

    return [
      {
        accessorKey: 'recordNumber',
        header: t('appointments.columns.recordNumber'),
        cell: ({ row }) => <span>{row.getValue('recordNumber')}</span>,
      },
      {
        id: 'provider',
        header: isHomecare
          ? t('appointments.columns.vendor')
          : t('appointments.columns.provider'),
        cell: ({ row }) => (
          <span className="font-medium">{row.original.providerName}</span>
        ),
      },
      {
        id: 'detail',
        header: isHomecare
          ? t('appointments.columns.service')
          : t('appointments.columns.detail'),
        cell: ({ row }) => <span>{row.original.speciality}</span>,
      },
      {
        accessorKey: 'date',
        header: isSecondOpinion
          ? t('appointments.columns.deliveryTime')
          : t('appointments.columns.appointmentDate'),
        cell: ({ row }) => (
          <span className="text-gray-600">{row.original.date}</span>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: isSecondOpinion
          ? t('appointments.columns.requestedAt')
          : t('appointments.columns.createdAt'),
        cell: ({ row }) => (
          <span className="text-gray-600">
            {formatDate(row.original.createdAt)}
          </span>
        ),
      },
      statusColumn,
    ];
  }, [activeTab, t]);

  const table = useReactTable({
    data: activeRecords,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: activePaginationSetter,
    manualPagination: true,
    pageCount: activePageCount,
    state: { pagination: activePagination },
    getRowId: (row) => row.id,
  });

  const emptyMessage = {
    standard: t('appointments.empty.standard'),
    second_opinion: t('appointments.empty.secondOpinion'),
    homecare: t('appointments.empty.homecare'),
    printing: t('appointments.empty.printing'),
  }[activeTab];

  return (
    <>
      <Header>
        <ProfileCard />
      </Header>
      <div className="mb-4 flex-1 space-y-6 p-4 md:p-0 md:pr-4">
        <h1 className="text-2xl font-bold">{t('appointments.pageTitle')}</h1>

        <div className="flex gap-2">
          {tabs
            .filter((tab) => {
              if (tab.key === 'homecare') return isHomecareEnabled;
              if (tab.key === 'printing') return isB2B;
              return true;
            })
            .map((tab) => (
              <Button
                key={tab.key}
                variant={activeTab === tab.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </Button>
            ))}
        </div>

        <Card>
          <CardContent>
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id} className="text-start">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {activeLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        <Loading />
                      </TableCell>
                    </TableRow>
                  ) : table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleRowClick(row.original)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id} className="text-start">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        {emptyMessage}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <div className="flex justify-center pt-4">
                <Pagination
                  currentPage={table.getState().pagination.pageIndex}
                  pageCount={activePageCount}
                  onPageChange={table.setPageIndex}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
